import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { Department, ProductionFlowRow } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'

interface FlowsPayload {
  departments: Department[]
  flows: ProductionFlowRow[]
}

/**
 * Which departments each product category actually visits. Order always
 * follows the global pipeline - a flow can skip sections, never reorder them.
 * Edits apply to new orders only; jobs already on the floor keep the flow
 * frozen on them at creation.
 */
export default function AdminFlows() {
  const toast = useToast()
  const queryClient = useQueryClient()
  // Unsaved toggles per category; absent key means "as loaded".
  const [draft, setDraft] = useState<Record<string, number[]>>({})

  const flows = useQuery({
    queryKey: ['admin-production-flows'],
    queryFn: async () => (await api.get<FlowsPayload>('/admin/production-flows')).data,
  })

  const save = useMutation({
    mutationFn: async (category: string) =>
      (await api.put('/admin/production-flows', { category, department_ids: draft[category] })).data,
    onSuccess: (res, category) => {
      queryClient.invalidateQueries({ queryKey: ['admin-production-flows'] })
      setDraft(({ [category]: _saved, ...rest }) => rest)
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  if (flows.isLoading) return <LogoLoader />

  const departments = flows.data?.departments ?? []
  const allIds = departments.map((d) => d.id)

  const selectedFor = (row: ProductionFlowRow) =>
    draft[row.category] ?? row.department_ids?.map(Number) ?? allIds

  const toggle = (row: ProductionFlowRow, departmentId: number) => {
    const current = selectedFor(row)
    const next = current.includes(departmentId)
      ? current.filter((id) => id !== departmentId)
      : [...current, departmentId]
    setDraft({ ...draft, [row.category]: next })
  }

  return (
    <div>
      <PageHeader
        title="Production Flows"
        subtitle="Pick which sections each product category goes through - skipped sections never see the job, and the ETD only counts the selected ones"
      />

      <div className="space-y-4">
        {flows.data?.flows.map((row) => {
          const selected = selectedFor(row)
          const dirty = row.category in draft
          const runsAll = selected.length === departments.length

          return (
            <Card key={row.category}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <SectionLabel>{row.category}</SectionLabel>
                  <p className="text-xs text-muted">
                    {runsAll
                      ? 'Runs the full chain'
                      : `Runs ${selected.length} of ${departments.length} sections`}
                    {' - applies to new orders only'}
                  </p>
                </div>
                <Button
                  disabled={!dirty || selected.length === 0}
                  loading={save.isPending && save.variables === row.category}
                  onClick={() => save.mutate(row.category)}
                >
                  Save
                </Button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {departments.map((dept, i) => {
                  const on = selected.includes(dept.id)
                  return (
                    <span key={dept.id} className="flex items-center gap-1.5">
                      {i > 0 && <ArrowRight className="size-3.5 text-foreground/30" />}
                      <button
                        type="button"
                        onClick={() => toggle(row, dept.id)}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-sm font-semibold',
                          on
                            ? 'border-primary/60 bg-primary/20 text-primary'
                            : 'border-line text-muted line-through opacity-60 hover:opacity-100',
                        )}
                      >
                        {dept.sequence}. {dept.name}
                      </button>
                    </span>
                  )
                })}
              </div>
              {selected.length === 0 && (
                <p className="mt-2 text-xs font-semibold text-status-danger">
                  A flow needs at least one section.
                </p>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}
